import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import { FaTimes, FaPen } from 'react-icons/fa';
import axios from 'axios';

const StatusLabel = ({ caseId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('Not On Hold');
  const [customStatus, setCustomStatus] = useState('');
  const [currentStatus, setCurrentStatus] = useState('Not On Hold');
  const [statusUpdatedAt, setStatusUpdatedAt] = useState(null);

  // Mobile dropdown open/close state
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);

  const mobileDropdownRef = useRef(null);

  const holdOptions = [
    'Not On Hold',
    'Metal try-in',
    'Bite block',
    'Die trim',
    'Frame try-in',
    'Problem with case',
    'Hold Case',
    'In lab - no date yet',
    'Hold for model mod.',
    'Hold for study mod.',
    'Hold for work. mod.',
    'Hold for picture',
    'Hold for shade',
    'Hold for bite',
    'Hold for components',
    'Hold for Impression',
    'Hold for partial',
    'Wax try-in',
    'Dr. requested call',
    'Bisque bake try-in',
    'Other'
  ];

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}`
        );

        const fetchedStatus =
          response.data.status || 'in progress';

        const fetchedUpdatedAt =
          response.data.statusUpdatedAt;

        if (fetchedStatus === 'in progress') {
          setSelectedStatus('Not On Hold');
          setCurrentStatus('Not On Hold');
          setCustomStatus('');
        } else {
          if (holdOptions.includes(fetchedStatus)) {
            setSelectedStatus(fetchedStatus);
            setCustomStatus('');
          } else {
            setSelectedStatus('Other');
            setCustomStatus(fetchedStatus);
          }

          setCurrentStatus(fetchedStatus);
        }

        setStatusUpdatedAt(
          fetchedUpdatedAt
            ? new Date(fetchedUpdatedAt).toLocaleString()
            : null
        );
      } catch (error) {
        console.error(
          '❌ Error fetching case status:',
          error
        );
      }
    };

    fetchStatus();
  }, [caseId]);

  /*
   * Close mobile dropdown when clicking outside it.
   */
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        mobileDropdownRef.current &&
        !mobileDropdownRef.current.contains(event.target)
      ) {
        setIsMobileDropdownOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleOutsideClick
      );
    };
  }, []);

  const openModal = () => {
    setIsModalOpen(true);
    setIsMobileDropdownOpen(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsMobileDropdownOpen(false);
  };

  /*
   * Same status selection logic for both
   * desktop native select and mobile dropdown.
   */
  const handleStatusChange = (value) => {
    setSelectedStatus(value);

    if (value !== 'Other') {
      setCustomStatus('');
    }

    setIsMobileDropdownOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newStatus;

    if (selectedStatus === 'Not On Hold') {
      newStatus = 'in progress';
    } else if (selectedStatus === 'Other') {
      if (!customStatus.trim()) {
        alert('Please enter a custom status');
        return;
      }

      newStatus = customStatus;
    } else {
      newStatus = selectedStatus;
    }

    try {
      const response = await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/cases/updateStatus/${caseId}`,
        {
          status: newStatus
        }
      );

      alert('Status updated successfully');

      const updatedCase = response.data.case;

      const updatedStatus =
        updatedCase.status === 'in progress'
          ? 'Not On Hold'
          : updatedCase.status;

      setCurrentStatus(updatedStatus);

      setStatusUpdatedAt(
        updatedCase.statusUpdatedAt
          ? new Date(
              updatedCase.statusUpdatedAt
            ).toLocaleString()
          : null
      );

      closeModal();
    } catch (error) {
      console.error(
        '❌ Error updating status:',
        error
      );

      alert('Failed to update status');
    }
  };

  return (
    <div className="status-label-wrapper">
      <style>{`

        /* =========================================================
           DESKTOP
           EXISTING DESKTOP BEHAVIOR PRESERVED
        ========================================================= */

        .status-label-wrapper {
          width: 100%;
          box-sizing: border-box;
          position: relative;

          margin-top: 18px;
          margin-bottom: 18px;
        }

        .status-label-title {
          margin: 0;
          padding: 0;
          font-size: 14px;
          margin-bottom: 6px;
        }

        .status-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .current-status-text {
          margin: 0;
          font-size: 14px;
        }

        .status-updated-text {
          margin: 2px 0 0;
          font-size: 10px;
        }

        /* =========================================================
           CHANGE BUTTON → pencil icon (BLUE, like Provider icon)
        ========================================================= */

        .status-change-button {
          cursor: pointer;
          background-color: transparent !important;
          background: transparent !important;
          color: #004aad !important;      /* ✅ BLUE icon */
          padding: 4px 6px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          font-size: 16px;
          line-height: 1;
        }

        .status-change-button:hover {
          background-color: transparent !important;
          color: #003382 !important;      /* ✅ darker blue on hover */
        }

        .status-change-icon {
          display: block;
          color: #004aad !important;
        }

        /* =========================================================
           MODAL - DESKTOP
        ========================================================= */

        .status-modal-overlay {
          position: fixed !important;
          inset: 0 !important;
          background: rgba(0, 0, 0, 0.5);

          z-index: 1000;

          display: flex;
          align-items: center;
          justify-content: center;

          padding: 10px;
          box-sizing: border-box;

          overflow-y: auto;
        }

        .status-modal {
          position: relative !important;
          inset: auto !important;

          width: 30%;
          max-width: 500px;
          min-width: 0;

          margin: 0 auto;

          padding: 0 !important;

          border-radius: 4px;

          height: auto;

          max-height: 90vh;

          overflow-y: auto;
          overflow-x: hidden;

          box-sizing: border-box;

          background: white;

          border: 1px solid #cccccc;

          outline: none;
        }

        .status-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;

          padding: 7px 10px;

          background: #004aad;
          color: white;

          border-radius: 4px 4px 0 0;
        }

        .status-modal-title {
          margin: 0;
          font-size: 15px;
          font-weight: 600;
        }

        .status-modal-close {
          width: 25px;
          height: 25px;

          display: flex;
          align-items: center;
          justify-content: center;

          padding: 0;

          background: transparent;
          border: none;

          color: white;

          cursor: pointer;

          font-size: 14px;
        }

        .status-modal-form {
          width: 100%;
          padding: 15px;
          box-sizing: border-box;
        }

        .status-form-group {
          width: 100%;
          margin-bottom: 15px;
          box-sizing: border-box;
        }

        .status-form-label {
          display: block;

          margin-bottom: 5px;

          font-size: 12px;
          font-weight: 600;
        }

        /* =========================================================
           DESKTOP NATIVE SELECT
        ========================================================= */

        .desktop-status-select {
          width: 100%;
          max-width: 100%;
          min-width: 0;

          padding: 8px;

          border: 1px solid #004aad;
          border-radius: 4px;

          background: #f9f9f9;

          font-size: 16px;
          line-height: 1.2;

          box-sizing: border-box;

          appearance: auto;
          -webkit-appearance: menulist;
          -moz-appearance: menulist;

          cursor: pointer;
        }

        .desktop-status-select option {
          font-size: 16px;
        }

        /* =========================================================
           TEXTAREA
        ========================================================= */

        .status-textarea {
          width: 100%;
          max-width: 100%;
          min-width: 0;

          padding: 8px;

          border: 1px solid #cccccc;
          border-radius: 4px;

          font-family: inherit;
          font-size: 13px;

          box-sizing: border-box;

          resize: vertical;
        }

        /* =========================================================
           ACTIONS
        ========================================================= */

        .status-modal-actions {
          width: 100%;

          display: flex;
          justify-content: flex-end;
          align-items: center;

          gap: 5px;
        }

        .status-cancel-button,
        .status-save-button {
          border: none;
          border-radius: 4px;

          padding: 5px 9px;

          font-size: 11px;

          cursor: pointer;
        }

        .status-cancel-button {
          background: #cccccc;
          color: #222;
        }

        .status-save-button {
          background: #004aad;
          color: white;
        }


        /* =========================================================
           MOBILE DROPDOWN
        ========================================================= */

        .mobile-status-dropdown {
          display: none;
        }

        .mobile-status-dropdown-button {
          display: flex;
          align-items: center;
          justify-content: space-between;

          width: 100%;

          min-height: 32px;

          padding: 7px 8px;

          background: #f9f9f9;

          border: 1px solid #004aad;
          border-radius: 4px;

          color: #222;

          font-family: inherit;

          font-size: 13px;

          text-align: left;

          cursor: pointer;

          box-sizing: border-box;
        }

        .mobile-status-dropdown-arrow {
          flex-shrink: 0;

          margin-left: 5px;

          font-size: 9px;
        }

        .mobile-status-dropdown-menu {
          width: 100%;

          margin-top: 2px;

          background: white;

          border: 1px solid #004aad;

          border-radius: 3px;

          box-sizing: border-box;

          position: relative;

          z-index: 100002;

          max-height: 220px;

          overflow-x: hidden;
          overflow-y: auto;

          -webkit-overflow-scrolling: touch;

          box-shadow: 0 2px 5px rgba(0, 0, 0, 0.18);
        }

        .mobile-status-dropdown-option {
          width: 100%;

          display: block;

          padding: 6px 7px;

          margin: 0;

          border: none;

          background: white;

          color: #222;

          font-family: inherit;

          font-size: 11px;

          line-height: 1.2;

          text-align: left;

          white-space: normal;

          overflow-wrap: anywhere;

          cursor: pointer;

          box-sizing: border-box;
        }

        .mobile-status-dropdown-option:hover {
          background: #e9f4ff;
        }

        .mobile-status-dropdown-option.selected {
          background: #b7e3f2;
        }


        /* =========================================================
           MOBILE ONLY
        ========================================================= */

        @media (max-width: 768px) {

          .status-label-wrapper {
            margin-top: 14px;
            margin-bottom: 14px;
          }

          .status-modal-overlay {
            padding: 8px;

            overflow-x: hidden;
            overflow-y: auto;
          }

          .status-modal {
            width: calc(100vw - 16px) !important;
            max-width: calc(100vw - 16px) !important;

            max-height: none !important;

            overflow: visible !important;

            margin: 0 auto !important;
          }

          .status-modal-form {
            padding: 10px;

            overflow: visible !important;
          }

          .status-form-group {
            overflow: visible !important;
          }

          .desktop-status-select {
            display: none !important;
          }

          .mobile-status-dropdown {
            display: block;
            width: 100%;
            position: relative;
          }

          .mobile-status-dropdown-button {
            min-height: 27px;

            padding: 5px 6px;

            font-size: 10px;
          }

          .mobile-status-dropdown-option {
            padding: 5px 6px;

            font-size: 9px;
          }

          .mobile-status-dropdown-menu {
            max-height: 210px;

            width: 100%;
          }

          .status-form-label {
            font-size: 10px;

            margin-bottom: 4px;
          }

          .status-textarea {
            font-size: 10px;

            padding: 6px;
          }

          .status-modal-title {
            font-size: 13px;
          }

          .status-modal-close {
            width: 22px;
            height: 22px;

            font-size: 12px;
          }

          .status-cancel-button,
          .status-save-button {
            padding: 4px 7px;

            font-size: 9px;
          }
        }


        /* =========================================================
           SMALL MOBILE
        ========================================================= */

        @media (max-width: 576px) {

          .status-label-wrapper {
            margin-top: 12px;
            margin-bottom: 12px;
          }

          .status-modal-overlay {
            padding: 6px;
          }

          .status-modal {
            width: calc(100vw - 12px) !important;
            max-width: calc(100vw - 12px) !important;
          }

          .status-modal-header {
            padding: 5px 7px;
          }

          .status-modal-title {
            font-size: 12px;
          }

          .status-modal-close {
            width: 20px;
            height: 20px;

            font-size: 11px;
          }

          .status-modal-form {
            padding: 8px;
          }

          .status-form-label {
            font-size: 9px;

            margin-bottom: 3px;
          }

          .mobile-status-dropdown-button {
            min-height: 25px;

            padding: 4px 5px;

            font-size: 9px;
          }

          .mobile-status-dropdown-option {
            padding: 5px;

            font-size: 8px;

            line-height: 1.25;
          }

          .mobile-status-dropdown-menu {
            max-height: 200px;
          }

          .status-textarea {
            font-size: 9px;

            padding: 5px;
          }

          .status-cancel-button,
          .status-save-button {
            padding: 3px 6px;

            font-size: 8px;
          }
        }


        /* =========================================================
           VERY SMALL SCREEN
        ========================================================= */

        @media (max-width: 400px) {

          .status-label-wrapper {
            margin-top: 10px;
            margin-bottom: 10px;
          }

          .status-modal-overlay {
            padding: 4px;
          }

          .status-modal {
            width: calc(100vw - 8px) !important;
            max-width: calc(100vw - 8px) !important;
          }

          .status-modal-header {
            padding: 4px 6px;
          }

          .status-modal-title {
            font-size: 11px;
          }

          .status-modal-close {
            width: 18px;
            height: 18px;

            font-size: 10px;
          }

          .status-modal-form {
            padding: 7px;
          }

          .status-form-label {
            font-size: 8px;
          }

          .mobile-status-dropdown-button {
            min-height: 23px;

            padding: 3px 4px;

            font-size: 8px;
          }

          .mobile-status-dropdown-option {
            padding: 4px;

            font-size: 7px;
          }

          .mobile-status-dropdown-menu {
            max-height: 190px;
          }

          .status-textarea {
            font-size: 8px;

            padding: 4px;
          }

          .status-cancel-button,
          .status-save-button {
            padding: 3px 5px;

            font-size: 7px;
          }
        }


        /* =========================================================
           320px
        ========================================================= */

        @media (max-width: 320px) {

          .status-label-wrapper {
            margin-top: 8px;
            margin-bottom: 8px;
          }

          .status-modal-overlay {
            padding: 3px;
          }

          .status-modal {
            width: calc(100vw - 6px) !important;
            max-width: calc(100vw - 6px) !important;
          }

          .status-modal-header {
            padding: 3px 5px;
          }

          .status-modal-title {
            font-size: 10px;
          }

          .status-modal-close {
            width: 17px;
            height: 17px;

            font-size: 9px;
          }

          .status-modal-form {
            padding: 6px;
          }

          .status-form-label {
            font-size: 7px;
          }

          .mobile-status-dropdown-button {
            min-height: 21px;

            padding: 2px 3px;

            font-size: 7px;
          }

          .mobile-status-dropdown-option {
            padding: 4px 3px;

            font-size: 6px;
          }

          .mobile-status-dropdown-menu {
            max-height: 180px;
          }

          .status-textarea {
            font-size: 7px;

            padding: 3px;
          }

          .status-cancel-button,
          .status-save-button {
            padding: 2px 4px;

            font-size: 6px;
          }
        }


        /* =========================================================
           280px
        ========================================================= */

        @media (max-width: 280px) {

          .status-label-wrapper {
            margin-top: 6px;
            margin-bottom: 6px;
          }

          .status-modal-overlay {
            padding: 2px;
          }

          .status-modal {
            width: calc(100vw - 4px) !important;
            max-width: calc(100vw - 4px) !important;
          }

          .status-modal-header {
            padding: 3px 4px;
          }

          .status-modal-title {
            font-size: 9px;
          }

          .status-modal-close {
            width: 16px;
            height: 16px;

            font-size: 8px;
          }

          .status-modal-form {
            padding: 5px;
          }

          .status-form-label {
            font-size: 7px;
          }

          .mobile-status-dropdown-button {
            min-height: 20px;

            padding: 2px 3px;

            font-size: 7px;
          }

          .mobile-status-dropdown-option {
            padding: 3px;

            font-size: 6px;
          }

          .mobile-status-dropdown-menu {
            max-height: 170px;
          }

          .status-textarea {
            font-size: 7px;

            padding: 3px;
          }

          .status-cancel-button,
          .status-save-button {
            padding: 2px 4px;

            font-size: 6px;
          }
        }

      `}</style>


      {/* =========================================================
          STATUS DISPLAY
      ========================================================= */}

      <p className="status-label-title">
        Status:
      </p>

      <div className="status-row">

        <p className="current-status-text">
          {currentStatus}
        </p>

        {/* ✅ Pencil icon (FaPen) in BLUE color, like Provider icon */}
        <button
          className="status-change-button"
          onClick={openModal}
          title="Change Status"
          aria-label="Change Status"
        >
          <FaPen className="status-change-icon" />
        </button>

      </div>

      {statusUpdatedAt && (
        <p className="status-updated-text">
          Last updated: {statusUpdatedAt}
        </p>
      )}


      {/* =========================================================
          MODAL
      ========================================================= */}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="On Hold Status"
        className="status-modal"
        overlayClassName="status-modal-overlay"
        shouldCloseOnOverlayClick={true}
      >

        <div className="status-modal-header">

          <h4 className="status-modal-title">
            On Hold Status
          </h4>

          <button
            type="button"
            onClick={closeModal}
            className="status-modal-close"
            aria-label="Close"
          >
            <FaTimes />
          </button>

        </div>


        <form
          onSubmit={handleSubmit}
          className="status-modal-form"
        >

          {/* =====================================================
              STATUS
          ===================================================== */}

          <div className="status-form-group">

            <label className="status-form-label">
              Status *
            </label>


            {/* ===================================================
                DESKTOP NATIVE SELECT
            =================================================== */}

            <select
              value={selectedStatus}
              onChange={(e) =>
                handleStatusChange(e.target.value)
              }
              className="desktop-status-select"
              title={selectedStatus}
            >

              {holdOptions.map((option) => (
                <option
                  key={option}
                  value={option}
                  title={option}
                >
                  {option}
                </option>
              ))}

            </select>


            {/* ===================================================
                MOBILE DROPDOWN
            =================================================== */}

            <div
              className="mobile-status-dropdown"
              ref={mobileDropdownRef}
            >

              <button
                type="button"
                className="mobile-status-dropdown-button"
                onClick={() =>
                  setIsMobileDropdownOpen(
                    (previous) => !previous
                  )
                }
                aria-haspopup="listbox"
                aria-expanded={
                  isMobileDropdownOpen
                }
              >

                <span>
                  {selectedStatus}
                </span>

                <span className="mobile-status-dropdown-arrow">
                  {isMobileDropdownOpen
                    ? '▲'
                    : '▼'}
                </span>

              </button>


              {isMobileDropdownOpen && (
                <div
                  className="mobile-status-dropdown-menu"
                  role="listbox"
                >

                  {holdOptions.map((option) => (

                    <button
                      key={option}
                      type="button"
                      className={
                        `mobile-status-dropdown-option ${
                          selectedStatus === option
                            ? 'selected'
                            : ''
                        }`
                      }
                      onClick={() =>
                        handleStatusChange(option)
                      }
                      role="option"
                      aria-selected={
                        selectedStatus === option
                      }
                    >
                      {option}
                    </button>

                  ))}

                </div>
              )}

            </div>

          </div>


          {/* =====================================================
              CUSTOM STATUS
          ===================================================== */}

          {selectedStatus === 'Other' && (

            <div className="status-form-group">

              <label className="status-form-label">
                Custom Status *
              </label>

              <textarea
                value={customStatus}
                onChange={(e) =>
                  setCustomStatus(e.target.value)
                }
                rows={4}
                className="status-textarea"
              />

            </div>

          )}


          {/* =====================================================
              BUTTONS
          ===================================================== */}

          <div className="status-modal-actions">

            <button
              type="button"
              onClick={closeModal}
              className="status-cancel-button"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="status-save-button"
            >
              Save
            </button>

          </div>

        </form>

      </Modal>

    </div>
  );
};

export default StatusLabel;